class B2BTradeTable extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.searchInput = this.querySelector('[data-b2b-search]');
    this.sortSelect = this.querySelector('[data-b2b-sort]');
    this.countNode = this.querySelector('[data-b2b-count]');
    this.noResultsNode = this.querySelector('[data-b2b-no-results]');
    this.tbody = this.querySelector('tbody');
    this.rows = Array.from(this.querySelectorAll('[data-b2b-row]'));
    this.variantSelects = Array.from(this.querySelectorAll('[data-b2b-variant-select]'));
    this.typeSelect = this.querySelector('[data-b2b-type]');
    this.producerSelect = this.querySelector('[data-b2b-producer]');
    this.stockInput = this.querySelector('[data-b2b-stock]');
    this.shareStatus = this.querySelector('[data-b2b-share-status]');
    this.collator = new Intl.Collator('is', { numeric: true, sensitivity: 'base' });

    if (!this.tbody || this.rows.length === 0) return;

    this.populateFilters();
    this.restoreState();
    this.searchInput?.addEventListener('input', () => this.applyState());
    [this.typeSelect, this.producerSelect, this.stockInput, this.sortSelect].forEach((input) => {
      input?.addEventListener('change', () => this.applyState());
    });
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-b2b-variant-select]')) {
        this.updateSelectedVariant(event.target);
        this.sortRows();
      }
    });
    this.querySelector('[data-b2b-reset]')?.addEventListener('click', () => {
      this.searchInput.value = '';
      this.typeSelect.value = '';
      this.producerSelect.value = '';
      this.stockInput.checked = false;
      this.sortSelect.value = 'wine-asc';
      this.applyState();
    });
    this.querySelector('[data-b2b-share]')?.addEventListener('click', () => this.shareSelection());
    this.onPopState = () => {
      this.restoreState();
      this.applyState(false);
    };
    window.addEventListener('popstate', this.onPopState);
    this.applyState(false);
    this.loadRemainingPages();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
  }

  normalize(value) {
    return (value || '').toLocaleLowerCase('is').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/ð/g, 'd').replace(/þ/g, 'th').replace(/æ/g, 'ae').replace(/ø/g, 'o');
  }

  isWine(row) {
    return /vín|vin|púrt/i.test(row.dataset.type || '');
  }

  populateFilters() {
    const populate = (select, entries, initial) => {
      if (!select) return;
      const previous = select.value;
      select.replaceChildren(new Option(initial, ''));
      entries.forEach(([value, label]) => select.add(new Option(label, value)));
      select.value = previous;
    };
    const types = [...new Set(this.rows.map((row) => row.dataset.type).filter(Boolean))];
    types.sort((a, b) => this.collator.compare(a, b));
    populate(this.typeSelect, [['wine', 'Allt vín'], ...types.map((type) => [type, type])], 'Allar vörur');
    const producers = new Map(this.rows.map((row) => [row.dataset.producer, row.dataset.producerLabel]));
    populate(this.producerSelect, [...producers].filter(([value]) => value)
      .sort((a, b) => this.collator.compare(a[1], b[1])), 'Allir framleiðendur');
  }

  restoreState() {
    const params = new URL(window.location.href).searchParams;
    if (this.searchInput) this.searchInput.value = params.get('bb_q') || '';
    if (this.typeSelect) this.typeSelect.value = params.get('bb_type') || '';
    if (this.producerSelect) this.producerSelect.value = params.get('bb_producer') || '';
    if (this.stockInput) this.stockInput.checked = params.get('bb_stock') === '1';
    if (this.sortSelect) {
      const sort = params.get('bb_sort') || 'wine-asc';
      this.sortSelect.value = [...this.sortSelect.options].some((option) => option.value === sort) ? sort : 'wine-asc';
    }
  }

  selectionUrl() {
    const url = new URL(window.location.href);
    const values = {
      bb_q: this.searchInput?.value.trim(),
      bb_type: this.typeSelect?.value,
      bb_producer: this.producerSelect?.value,
      bb_stock: this.stockInput?.checked ? '1' : '',
      bb_sort: this.sortSelect?.value === 'wine-asc' ? '' : this.sortSelect?.value,
    };
    Object.entries(values).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value);
      else url.searchParams.delete(key);
    });
    return url;
  }

  applyState(updateUrl = true) {
    this.sortRows();
    this.filterRows();
    if (updateUrl) history.replaceState(null, '', this.selectionUrl());
    if (this.shareStatus) this.shareStatus.textContent = '';
    const fallback = this.querySelector('[data-b2b-share-fallback]');
    if (fallback) fallback.hidden = true;
  }

  async shareSelection() {
    const url = this.selectionUrl();
    history.replaceState(null, '', url);
    try {
      await navigator.clipboard.writeText(url.href);
      this.shareStatus.textContent = 'Hlekkur afritaður';
    } catch {
      const input = this.querySelector('[data-b2b-share-url]');
      const fallback = this.querySelector('[data-b2b-share-fallback]');
      if (input && fallback) {
        fallback.hidden = false;
        input.value = url.href;
        input.focus();
        input.select();
      }
    }
  }

  async loadRemainingPages() {
    const pageCount = Number(this.dataset.pageCount || 1);
    if (pageCount <= 1) return;
    this.loading = true;
    this.filterRows();
    try {
      for (let page = 1; page <= pageCount; page += 1) {
        if (page === Number(this.dataset.currentPage || 1)) continue;
        const url = new URL(window.location.href);
        url.searchParams.set('page', page);
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Catalog page unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const rows = [...doc.querySelectorAll('b2b-trade-table [data-b2b-row]')];
        if (!rows.length) throw new Error('Catalog page missing');
        const ids = new Set(this.rows.map((row) => row.dataset.productId));
        rows.filter((row) => !ids.has(row.dataset.productId)).forEach((row) => this.tbody.append(row));
        this.rows = [...this.tbody.querySelectorAll('[data-b2b-row]')];
      }
      this.populateFilters();
      this.restoreState();
      this.closest('.page-width')?.querySelector('[data-b2b-pagination-note]')?.setAttribute('hidden', '');
      const pagination = this.closest('.page-width')?.querySelector('.pagination-wrapper');
      if (pagination) pagination.hidden = true;
    } catch {
      this.loadFailed = true;
    } finally {
      this.loading = false;
      this.applyState(false);
    }
  }

  filterRows() {
    const words = this.normalize(this.searchInput?.value).trim().split(/\s+/).filter(Boolean);
    const type = this.typeSelect?.value;
    const producer = this.producerSelect?.value;
    let visibleRows = 0;

    this.rows.forEach((row) => {
      const text = this.normalize(row.dataset.search);
      const isVisible = words.every((word) => text.includes(word))
        && (!type || (type === 'wine' ? this.isWine(row) : row.dataset.type === type))
        && (!producer || row.dataset.producer === producer)
        && (!this.stockInput?.checked || Number(row.dataset.stock) > 0);
      row.hidden = !isVisible;

      if (isVisible) visibleRows += 1;
    });

    if (this.countNode) {
      const noun = visibleRows === 1 ? 'vara' : 'vörur';
      this.countNode.textContent = `${visibleRows} ${noun}${this.loading ? ' · Hleð fleiri vörum…' : ''}${this.loadFailed ? ' · Ekki tókst að sækja allar vörur. Fleiri vörur eru á næstu síðum.' : ''}`;
    }

    if (this.noResultsNode) {
      this.noResultsNode.hidden = visibleRows !== 0;
    }
  }

  sortRows() {
    const value = this.sortSelect?.value || 'title-asc';
    const [key, direction] = value.split('-');
    const multiplier = direction === 'desc' ? -1 : 1;

    const sortedRows = [...this.rows].sort((rowA, rowB) => {
      if (key === 'wine') {
        const order = ['kampavín', 'freyðivín', 'hvítvín', 'rósavín', 'rauðvín', 'sætvín', 'styrkt vín', 'púrtvín'];
        const rank = (row) => {
          const index = order.indexOf((row.dataset.type || '').toLocaleLowerCase('is'));
          return index < 0 ? order.length : index;
        };
        return rank(rowA) - rank(rowB)
          || this.collator.compare(rowA.dataset.type || '', rowB.dataset.type || '')
          || this.collator.compare(rowA.dataset.producer || '', rowB.dataset.producer || '')
          || Number(rowA.dataset.price) - Number(rowB.dataset.price)
          || this.collator.compare(rowA.dataset.title, rowB.dataset.title);
      }
      if (key === 'price' || key === 'stock') {
        const numberA = Number(rowA.dataset[key] || 0);
        const numberB = Number(rowB.dataset[key] || 0);
        return (numberA - numberB) * multiplier;
      }

      const textA = rowA.dataset[key] || '';
      const textB = rowB.dataset[key] || '';
      return this.collator.compare(textA, textB) * multiplier;
    });

    sortedRows.forEach((row) => this.tbody.append(row));
    this.rows = sortedRows;
  }

  updateSelectedVariant(select) {
    const selectedOption = select.selectedOptions?.[0];
    const row = select.closest('[data-b2b-row]');

    if (!selectedOption || !row) return;

    const variantInput = row.querySelector('[data-b2b-variant-id]');
    const quantityInput = row.querySelector('[data-b2b-qty]');
    const submitButton = row.querySelector('[data-b2b-submit]');
    const priceNode = row.querySelector('[data-b2b-price]');
    const isOrderable = selectedOption.dataset.orderable === 'true';
    const variantTitle = selectedOption.dataset.title || '';
    const min = selectedOption.dataset.min || '1';
    const step = selectedOption.dataset.step || '1';
    const max = selectedOption.dataset.max;

    if (variantInput) variantInput.value = selectedOption.value;
    if (priceNode) priceNode.innerHTML = selectedOption.dataset.priceHtml || '';

    if (quantityInput) {
      quantityInput.value = min;
      quantityInput.min = min;
      quantityInput.step = step;

      if (max) {
        quantityInput.max = max;
      } else {
        quantityInput.removeAttribute('max');
      }

      quantityInput.disabled = !isOrderable;

      if (variantTitle) {
        const productTitle = row.dataset.productTitle || row.dataset.title || '';
        quantityInput.setAttribute('aria-label', `Magn fyrir ${productTitle} ${variantTitle}`);
      }
    }

    if (submitButton) {
      submitButton.disabled = !isOrderable;
      submitButton.textContent = isOrderable ? 'Bæta við' : 'Uppselt';
    }

    row.dataset.price = selectedOption.dataset.priceCents || row.dataset.price;
    row.dataset.variant = variantTitle.toLowerCase();
  }
}

if (!customElements.get('b2b-trade-table')) {
  customElements.define('b2b-trade-table', B2BTradeTable);
}
